import Sdk from 'api.digitalpages.module.sdk.api';

class ReadingListService {
  // Carregar todas as listas de leitura (tags) do usuário
  static async loadReadingLists() {
    try {
      const response = await Sdk.Api.dynamic.bridge('cms/v1.1/user/data/tags', null, 'GET');
      return response.filter(tag => tag.name !== '###InternalFavorites###').map(tag => ({
        uid: tag.uid,
        name: tag.name,
        description: tag.description || ''
      }));
    } catch (error) {
      console.error('Error loading reading lists:', error);
      return [];
    }
  }

  // Criar nova lista de leitura
  static async createReadingList(name, description = '') {
    try {
      // const userInfo = await Sdk.Api.dynamic.bridge('auth/v1.0/user/me', null, "GET");
      const connectors = Sdk.Api.authorization.activeProject.connectors;
      const userDataConnector = connectors.find(connector => 
        connector.features?.includes('IConnectorUserData')
      );
      
      if (!userDataConnector) {
        throw new Error('UserData connector not found');
      }

      const createUrl = `cms/v1.1/user/connector/uid/${userDataConnector.uid}/tag`;
      const response = await Sdk.Api.dynamic.bridge(createUrl, {
        name,
        description
      }, 'POST');
      
      return response;
    } catch (error) {
      console.error('Error creating reading list:', error);
      throw error;
    }
  }

  // Adicionar livro a uma lista de leitura
  static async addBookToReadingList(bookUid, tagUid) {
    try {
      // Verificar se já existe UserData para este livro
      const checkUrl = `cms/v1.1/user/data?context_type=ManagedContent&target_type=ManagedContent&target_uid=${bookUid}&context_uid=${bookUid}`;
      const existingResponse = await Sdk.Api.dynamic.bridge(checkUrl, null, 'GET');
      const existingData = existingResponse.result;
      
      if (existingData.length === 0) {
        // Criar novo UserData
        // const userInfo = await Sdk.Api.dynamic.bridge('auth/v1.0/user/me', null, "GET");
        const connectors = Sdk.Api.authorization.activeProject.connectors;
        const userDataConnector = connectors?.find(connector => 
          connector.features?.includes('IConnectorUserData')
        );
        
        if (!userDataConnector) {
          throw new Error('UserData connector not found');
        }

        const createUrl = `cms/v1.1/user/connector/uid/${userDataConnector.uid}/data`;
        await Sdk.Api.dynamic.bridge(createUrl, {
          context_uid: bookUid,
          context_type: 'ManagedContent',
          target_uid: bookUid,
          target_type: 'ManagedContent',
          tags: [{ name: (await this.getTagById(tagUid)).name }]
        }, 'POST');
      } else {
        // Atualizar UserData existente
        const userData = existingData[0];
        const updateUrl = `cms/v1.1/user/data/uid/${userData.uid}`;
        
        if (!userData.tags) userData.tags = [];
        
        const tagExists = userData.tags.find(t => t.uid === tagUid);
        if (!tagExists) {
          const tagData = await this.getTagById(tagUid);
          userData.tags.push({ name: tagData.name });
          await Sdk.Api.dynamic.bridge(updateUrl, userData, 'PUT');
        }
      }
    } catch (error) {
      console.error('Error adding book to reading list:', error);
      throw error;
    }
  }

  static async getTagById(tagUid) {
    const tags = await this.loadReadingLists();
    return tags.find(tag => tag.uid === tagUid) || { name: 'Unknown Tag' };
  }

  // Remover livro de uma lista de leitura
  static async removeBookFromReadingList(bookUid, tagUid) {
    try {
      const checkUrl = `cms/v1.1/user/data?context_type=ManagedContent&target_type=ManagedContent&target_uid=${bookUid}&context_uid=${bookUid}`;
      const existingResponse = await Sdk.Api.dynamic.bridge(checkUrl, null, 'GET');
      const existingData = existingResponse.result;
      
      for (const userData of existingData) {
        if (!userData.tags) continue;
        
        userData.tags = userData.tags.filter(t => t.uid !== tagUid);
        const updateUrl = `cms/v1.1/user/data/uid/${userData.uid}`;
        await Sdk.Api.dynamic.bridge(updateUrl, userData, 'PUT');
      }
    } catch (error) {
      console.error('Error removing book from reading list:', error);
      throw error;
    }
  }

  static async getUserData() {
    const result = [];
    let page = 1;
    
    while (true) {
      const url = `cms/v1.1/user/data?page=${page}`;
      const response = await Sdk.Api.dynamic.bridge(url, null, 'GET');
      
      result.push(...response.result.map(serverData => ({
        uid: serverData.uid,
        contextType: serverData.context_type,
        contextUid: serverData.context_uid,
        targetType: serverData.target_type,
        targetUid: serverData.target_uid,
        tags: serverData.tags?.map(t => ({ uid: t.uid, name: t.name }))
      })));
      
      if (!response.next_page_query) break;
      page++;
    }
    
    return result;
  }

  // Verificar em quais listas um livro está
  static async getBookReadingLists(bookUid) {
    try {
      const userData = await this.getUserData();
      const bookEntries = userData.filter(entry => 
        entry.contextUid === bookUid && 
        entry.tags && 
        entry.tags.length > 0
      );
      
      const tags = [];
      bookEntries.forEach(entry => {
        entry.tags.forEach(tag => {
          if (tag.name !== '###InternalFavorites###' && !tags.find(t => t.uid === tag.uid)) {
            tags.push(tag);
          }
        });
      });
      
      return tags;
    } catch (error) {
      console.error('Error getting book reading lists:', error);
      return [];
    }
  }
}

export default ReadingListService;